import {
  addDoc,
  collection,
  doc,
  getDoc,
  limit,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type Unsubscribe,
} from 'firebase/firestore';

import { auth, db, functions } from '../lib/firebase';
import { httpsCallable } from 'firebase/functions';
import type { UserProfile } from '../types';

const THREADS = 'bazaarDMs';
const clean = (value: unknown) => String(value ?? '').trim();
const nowMs = () => Date.now();

export interface BazaarDMThread {
  id: string;
  listingId: string;
  listingTitle: string;
  listingImage?: string;
  listingPriceCents?: number;
  listingCurrency?: string;
  buyerId: string;
  buyerName: string;
  buyerAvatar?: string;
  sellerId: string;
  sellerName: string;
  sellerAvatar?: string;
  participantIds: string[];
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  lastMessageText?: string;
  lastMessageSenderId?: string;
  lastMessageAt?: number;
  status: 'active' | 'archived' | string;
  listingStatus?: string;
  archivedAt?: number;
  lifecycleUpdatedAt?: number;
}


export interface BazaarDMSellerThread extends BazaarDMThread {
  sellerLastReadAt: number;
  hiddenBefore: number;
  unread: boolean;
}

export interface BazaarDMBuyerThread extends BazaarDMThread {
  buyerLastReadAt: number;
  hiddenBefore: number;
  unread: boolean;
}

export type BazaarDMReadState = {
  uid: string;
  lastReadAt: number;
  updatedAt: number;
  hiddenBefore: number;
  hiddenAt: number;
};

export interface BazaarDMMessage {
  id: string;
  threadId: string;
  senderId: string;
  text: string;
  createdAt: number;
  updatedAt: number;
  editedAt?: number;
  deletedAt?: number;
  status: 'visible' | 'deleted' | string;
  messageType?: 'offer' | string;
  offerId?: string;
  offerAction?: BazaarOfferAction;
  amountCents?: number;
  currency?: string;
  note?: string;
}

const stripUndefined = <T extends Record<string, any>>(input: T): T => {
  const out: Record<string, any> = {};
  Object.entries(input || {}).forEach(([key, value]) => {
    if (value !== undefined) out[key] = value;
  });
  return out as T;
};

function normalizeThread(id: string, data: any): BazaarDMThread {
  return {
    id,
    listingId: clean(data?.listingId),
    listingTitle: clean(data?.listingTitle) || 'Bazaar listing',
    listingImage: clean(data?.listingImage) || undefined,
    listingPriceCents: Number.isFinite(Number(data?.listingPriceCents)) ? Math.round(Number(data?.listingPriceCents)) : undefined,
    listingCurrency: clean(data?.listingCurrency).toLowerCase() || undefined,
    buyerId: clean(data?.buyerId),
    buyerName: clean(data?.buyerName) || 'Buyer',
    buyerAvatar: clean(data?.buyerAvatar) || undefined,
    sellerId: clean(data?.sellerId),
    sellerName: clean(data?.sellerName) || 'Seller',
    sellerAvatar: clean(data?.sellerAvatar) || undefined,
    participantIds: Array.isArray(data?.participantIds)
      ? data.participantIds.map((value: unknown) => clean(value)).filter(Boolean)
      : [],
    createdBy: clean(data?.createdBy),
    createdAt: Number(data?.createdAt || 0),
    updatedAt: Number(data?.updatedAt || 0),
    lastMessageText: clean(data?.lastMessageText) || undefined,
    lastMessageSenderId: clean(data?.lastMessageSenderId) || undefined,
    lastMessageAt: Number(data?.lastMessageAt || 0) || undefined,
    status: clean(data?.status) || 'active',
    listingStatus: clean(data?.listingStatus) || undefined,
    archivedAt: Number(data?.archivedAt || 0) || undefined,
    lifecycleUpdatedAt: Number(data?.lifecycleUpdatedAt || 0) || undefined,
  };
}

function normalizeMessage(id: string, data: any): BazaarDMMessage {
  return {
    id,
    threadId: clean(data?.threadId),
    senderId: clean(data?.senderId),
    text: String(data?.text ?? ''),
    createdAt: Number(data?.createdAt || 0),
    updatedAt: Number(data?.updatedAt || 0),
    editedAt: Number(data?.editedAt || 0) || undefined,
    deletedAt: Number(data?.deletedAt || 0) || undefined,
    status: clean(data?.status) || 'visible',
    messageType: clean(data?.messageType) || undefined,
    offerId: clean(data?.offerId) || undefined,
    offerAction: clean(data?.offerAction) as BazaarOfferAction || undefined,
    amountCents: Number.isFinite(Number(data?.amountCents)) ? Math.round(Number(data?.amountCents)) : undefined,
    currency: clean(data?.currency).toLowerCase() || undefined,
    note: clean(data?.note) || undefined,
  };
}

function requireAuthUid(): string {
  const uid = clean(auth.currentUser?.uid);
  if (!uid) throw new Error('You must be signed in to use Bazaar messages.');
  return uid;
}

export async function refreshMyBazaarThreadLifecycle(): Promise<{ ok: boolean; updated: number; checked: number }> {
  requireAuthUid();
  const callable = httpsCallable<Record<string, never>, { ok: boolean; updated: number; checked: number }>(
    functions,
    'refreshMyBazaarThreadLifecycle',
  );
  const result = await callable({});
  return result.data;
}

export function getBazaarDMThreadId(listingId: string, buyerUid: string): string {
  const listing = clean(listingId).replace(/\//g, '_');
  const buyer = clean(buyerUid).replace(/\//g, '_');
  if (!listing || !buyer) return '';
  return `${listing}__${buyer}`;
}

export function subscribeBazaarDMThreadState(
  threadId: string,
  currentUid: string,
  onChange: (state: { exists: boolean; thread: BazaarDMThread | null }) => void,
  onError?: (error: unknown) => void,
): Unsubscribe {
  const id = clean(threadId);
  const uid = clean(currentUid);
  if (!id || !uid) {
    onChange({ exists: false, thread: null });
    return () => {};
  }

  return onSnapshot(
    doc(db, THREADS, id),
    { includeMetadataChanges: true },
    (snapshot) => {
      if (!snapshot.exists()) {
        onChange({ exists: false, thread: null });
        return;
      }
      // Do not unlock child listeners from a latency-compensated local create/update.
      // Firestore can surface the parent thread locally before the server has
      // committed it; child queries that depend on the parent security rule can
      // otherwise race and receive permission-denied. includeMetadataChanges
      // below guarantees a second snapshot when the pending write is acknowledged.
      if (snapshot.metadata.hasPendingWrites) return;

      const thread = normalizeThread(snapshot.id, snapshot.data());
      if (!thread.participantIds.includes(uid)) {
        onChange({ exists: false, thread: null });
        return;
      }
      onChange({ exists: true, thread });
    },
    (error) => {
      console.warn('[bazaarDMs] thread-state subscription failed', { threadId: id, error });
      onError?.(error);
      onChange({ exists: false, thread: null });
    },
  );
}

export function subscribeBazaarDMMessages(
  threadId: string,
  onChange: (messages: BazaarDMMessage[]) => void,
  onError?: (error: unknown) => void,
  max = 200,
): Unsubscribe {
  const id = clean(threadId);
  if (!id) {
    onChange([]);
    return () => {};
  }

  return onSnapshot(
    query(collection(db, THREADS, id, 'messages'), limit(Math.max(1, Math.min(max, 250)))),
    (snapshot) => {
      const rows = snapshot.docs
        .map((row) => normalizeMessage(row.id, row.data()))
        .sort((a, b) => a.createdAt - b.createdAt);
      onChange(rows);
    },
    (error) => {
      console.error('[bazaarDMs] message subscription failed', error);
      onError?.(error);
      onChange([]);
    },
  );
}


function normalizeReadState(uid: string, data: any): BazaarDMReadState {
  return {
    uid,
    lastReadAt: Number(data?.lastReadAt || 0),
    updatedAt: Number(data?.updatedAt || 0),
    hiddenBefore: Number(data?.hiddenBefore || 0),
    hiddenAt: Number(data?.hiddenAt || 0),
  };
}

export function subscribeBazaarDMReadState(
  uid: string,
  threadId: string,
  onChange: (state: BazaarDMReadState) => void,
  onError?: (error: unknown) => void,
): Unsubscribe {
  const cleanUid = clean(uid);
  const id = clean(threadId);
  if (!cleanUid || !id) {
    onChange(normalizeReadState(cleanUid, null));
    return () => {};
  }
  return onSnapshot(
    doc(db, THREADS, id, 'reads', cleanUid),
    (snapshot) => onChange(normalizeReadState(cleanUid, snapshot.exists() ? snapshot.data() : null)),
    (error) => {
      console.warn('[bazaarDMs] read-state subscription failed', { threadId: id, error });
      onError?.(error);
      onChange(normalizeReadState(cleanUid, null));
    },
  );
}

export async function clearBazaarDMConversationForMe(threadId: string): Promise<void> {
  const uid = requireAuthUid();
  const id = clean(threadId);
  if (!id) throw new Error('Conversation not found.');

  const threadSnap = await getDoc(doc(db, THREADS, id));
  if (!threadSnap.exists()) throw new Error('Conversation not found.');
  const thread = normalizeThread(threadSnap.id, threadSnap.data());
  if (!thread.participantIds.includes(uid)) throw new Error('You do not have access to this conversation.');

  const now = nowMs();
  const cutoff = Math.max(now, Number(thread.lastMessageAt || 0));
  await setDoc(
    doc(db, THREADS, id, 'reads', uid),
    { uid, lastReadAt: cutoff, updatedAt: now, hiddenBefore: cutoff, hiddenAt: now },
    { merge: true },
  );
}


export function subscribeBazaarSellerInbox(
  sellerUid: string,
  onChange: (threads: BazaarDMSellerThread[]) => void,
  onError?: (error: unknown) => void,
): Unsubscribe {
  const seller = clean(sellerUid);
  if (!seller) {
    onChange([]);
    return () => {};
  }

  const threads = new Map<string, BazaarDMThread>();
  const reads = new Map<string, BazaarDMReadState>();
  const readUnsubs = new Map<string, Unsubscribe>();

  const emit = () => {
    const rows = Array.from(threads.values())
      .map((thread): BazaarDMSellerThread => {
        const readState = reads.get(thread.id) || normalizeReadState(seller, null);
        const sellerLastReadAt = Number(readState.lastReadAt || 0);
        const hiddenBefore = Number(readState.hiddenBefore || 0);
        const lastMessageAt = Number(thread.lastMessageAt || thread.updatedAt || 0);
        const unread = Boolean(
          thread.lastMessageSenderId &&
          thread.lastMessageSenderId !== seller &&
          lastMessageAt > Math.max(sellerLastReadAt, hiddenBefore)
        );
        return { ...thread, sellerLastReadAt, hiddenBefore, unread };
      })
      .filter((thread) => Number(thread.lastMessageAt || thread.updatedAt || 0) > Number(thread.hiddenBefore || 0))
      .sort((a, b) => Number(b.lastMessageAt || b.updatedAt || 0) - Number(a.lastMessageAt || a.updatedAt || 0));
    onChange(rows);
  };

  const syncReadListeners = () => {
    const wanted = new Set(threads.keys());
    for (const [threadId, unsubscribe] of readUnsubs) {
      if (wanted.has(threadId)) continue;
      unsubscribe();
      readUnsubs.delete(threadId);
      reads.delete(threadId);
    }

    for (const threadId of wanted) {
      if (readUnsubs.has(threadId)) continue;
      const unsubscribe = onSnapshot(
        doc(db, THREADS, threadId, 'reads', seller),
        (snapshot) => {
          reads.set(threadId, normalizeReadState(seller, snapshot.exists() ? snapshot.data() : null));
          emit();
        },
        (error) => {
          console.warn('[bazaarDMs] seller read-state subscription failed', { threadId, error });
          reads.set(threadId, normalizeReadState(seller, null));
          emit();
        },
      );
      readUnsubs.set(threadId, unsubscribe);
    }
  };

  const q = query(collection(db, THREADS), where('sellerId', '==', seller), limit(100));
  const threadsUnsub = onSnapshot(
    q,
    (snapshot) => {
      threads.clear();
      snapshot.docs.forEach((row) => {
        const thread = normalizeThread(row.id, row.data());
        if (thread.sellerId === seller) threads.set(thread.id, thread);
      });
      syncReadListeners();
      emit();
    },
    (error) => {
      console.error('[bazaarDMs] seller inbox subscription failed', error);
      onError?.(error);
      onChange([]);
    },
  );

  return () => {
    threadsUnsub();
    readUnsubs.forEach((unsubscribe) => unsubscribe());
    readUnsubs.clear();
  };
}


export function subscribeBazaarBuyerInbox(
  buyerUid: string,
  onChange: (threads: BazaarDMBuyerThread[]) => void,
  onError?: (error: unknown) => void,
): Unsubscribe {
  const buyer = clean(buyerUid);
  if (!buyer) {
    onChange([]);
    return () => {};
  }

  const threads = new Map<string, BazaarDMThread>();
  const reads = new Map<string, BazaarDMReadState>();
  const readUnsubs = new Map<string, Unsubscribe>();

  const emit = () => {
    const rows = Array.from(threads.values())
      .map((thread): BazaarDMBuyerThread => {
        const readState = reads.get(thread.id) || normalizeReadState(buyer, null);
        const buyerLastReadAt = Number(readState.lastReadAt || 0);
        const hiddenBefore = Number(readState.hiddenBefore || 0);
        const lastMessageAt = Number(thread.lastMessageAt || thread.updatedAt || 0);
        const unread = Boolean(
          thread.lastMessageSenderId &&
          thread.lastMessageSenderId !== buyer &&
          lastMessageAt > Math.max(buyerLastReadAt, hiddenBefore)
        );
        return { ...thread, buyerLastReadAt, hiddenBefore, unread };
      })
      .filter((thread) => Number(thread.lastMessageAt || thread.updatedAt || 0) > Number(thread.hiddenBefore || 0))
      .sort((a, b) => Number(b.lastMessageAt || b.updatedAt || 0) - Number(a.lastMessageAt || a.updatedAt || 0));
    onChange(rows);
  };

  const syncReadListeners = () => {
    const wanted = new Set(threads.keys());
    for (const [threadId, unsubscribe] of readUnsubs) {
      if (wanted.has(threadId)) continue;
      unsubscribe();
      readUnsubs.delete(threadId);
      reads.delete(threadId);
    }

    for (const threadId of wanted) {
      if (readUnsubs.has(threadId)) continue;
      const unsubscribe = onSnapshot(
        doc(db, THREADS, threadId, 'reads', buyer),
        (snapshot) => {
          reads.set(threadId, normalizeReadState(buyer, snapshot.exists() ? snapshot.data() : null));
          emit();
        },
        (error) => {
          console.warn('[bazaarDMs] buyer read-state subscription failed', { threadId, error });
          reads.set(threadId, normalizeReadState(buyer, null));
          emit();
        },
      );
      readUnsubs.set(threadId, unsubscribe);
    }
  };

  const q = query(collection(db, THREADS), where('buyerId', '==', buyer), limit(100));
  const threadsUnsub = onSnapshot(
    q,
    (snapshot) => {
      threads.clear();
      snapshot.docs.forEach((row) => {
        const thread = normalizeThread(row.id, row.data());
        if (thread.buyerId === buyer) threads.set(thread.id, thread);
      });
      syncReadListeners();
      emit();
    },
    (error) => {
      console.error('[bazaarDMs] buyer inbox subscription failed', error);
      onError?.(error);
      onChange([]);
    },
  );

  return () => {
    threadsUnsub();
    readUnsubs.forEach((unsubscribe) => unsubscribe());
    readUnsubs.clear();
  };
}

export function subscribeBazaarThreadsForListing(
  listingId: string,
  sellerUid: string,
  onChange: (threads: BazaarDMThread[]) => void,
  onError?: (error: unknown) => void,
): Unsubscribe {
  const listing = clean(listingId);
  const seller = clean(sellerUid);
  if (!listing || !seller) {
    onChange([]);
    return () => {};
  }

  // Single-field seller query avoids a new composite index. The listing filter
  // is applied client-side after Firestore rules have already constrained rows
  // to conversations where this seller is a participant.
  const q = query(
    collection(db, THREADS),
    where('sellerId', '==', seller),
    limit(100),
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const rows = snapshot.docs
        .map((row) => normalizeThread(row.id, row.data()))
        .filter((row) => row.sellerId === seller && row.listingId === listing)
        .sort((a, b) => Number(b.lastMessageAt || b.updatedAt || 0) - Number(a.lastMessageAt || a.updatedAt || 0));
      onChange(rows);
    },
    (error) => {
      console.error('[bazaarDMs] listing threads subscription failed', error);
      onError?.(error);
      onChange([]);
    },
  );
}

export async function markBazaarDMRead(uid: string, threadId: string): Promise<void> {
  const currentUid = requireAuthUid();
  const requestedUid = clean(uid);
  const id = clean(threadId);
  if (!id || requestedUid !== currentUid) return;

  const threadSnap = await getDoc(doc(db, THREADS, id));
  if (!threadSnap.exists()) return;
  const thread = normalizeThread(threadSnap.id, threadSnap.data());
  if (!thread.participantIds.includes(currentUid)) return;

  const now = nowMs();
  await setDoc(
    doc(db, THREADS, id, 'reads', currentUid),
    { uid: currentUid, lastReadAt: Math.max(now, Number(thread.lastMessageAt || 0)), updatedAt: now },
    { merge: true },
  );
}

export async function sendBazaarDMMessage(params: {
  currentUser: UserProfile;
  listingId: string;
  listingTitle: string;
  listingImage?: string;
  listingPriceCents?: number;
  listingCurrency?: string;
  sellerId: string;
  sellerName: string;
  sellerAvatar?: string;
  buyerId: string;
  buyerName: string;
  buyerAvatar?: string;
  text: string;
}): Promise<{ threadId: string; messageId: string }> {
  const senderUid = requireAuthUid();
  const profileUid = clean((params.currentUser as any)?.uid);
  if (profileUid && profileUid !== senderUid) throw new Error('Bazaar message identity mismatch.');

  const listingId = clean(params.listingId);
  const sellerId = clean(params.sellerId);
  const buyerId = clean(params.buyerId);
  const text = clean(params.text).slice(0, 2000);
  if (!listingId || !sellerId || !buyerId) throw new Error('Bazaar conversation details are incomplete.');
  if (sellerId === buyerId) throw new Error('Buyer and seller must be different accounts.');
  if (senderUid !== sellerId && senderUid !== buyerId) throw new Error('You do not have access to this Bazaar conversation.');
  if (!text) throw new Error('Write a message first.');

  const threadId = getBazaarDMThreadId(listingId, buyerId);
  if (!threadId) throw new Error('Could not build Bazaar conversation id.');

  const threadRef = doc(db, THREADS, threadId);
  const existing = await getDoc(threadRef);
  if (!existing.exists() && senderUid !== buyerId) {
    throw new Error('The buyer must start this Bazaar conversation.');
  }

  const now = nowMs();
  const messageRef = doc(collection(threadRef, 'messages'));
  const batch = writeBatch(db);

  if (!existing.exists()) {
    batch.set(threadRef, stripUndefined({
      listingId,
      listingTitle: clean(params.listingTitle) || 'Bazaar listing',
      listingImage: clean(params.listingImage) || undefined,
      listingPriceCents: Number.isFinite(Number(params.listingPriceCents)) ? Math.max(0, Math.round(Number(params.listingPriceCents))) : undefined,
      listingCurrency: clean(params.listingCurrency).toLowerCase() || undefined,
      buyerId,
      buyerName: clean(params.buyerName) || 'Buyer',
      buyerAvatar: clean(params.buyerAvatar) || undefined,
      sellerId,
      sellerName: clean(params.sellerName) || 'Seller',
      sellerAvatar: clean(params.sellerAvatar) || undefined,
      participantIds: [buyerId, sellerId].sort(),
      createdBy: senderUid,
      createdAt: now,
      updatedAt: now,
      lastMessageText: text,
      lastMessageSenderId: senderUid,
      lastMessageAt: now,
      status: 'active',
    }));
  } else {
    batch.update(threadRef, {
      updatedAt: now,
      lastMessageText: text,
      lastMessageSenderId: senderUid,
      lastMessageAt: now,
      status: 'active',
    });
  }

  batch.set(messageRef, {
    threadId,
    senderId: senderUid,
    text,
    createdAt: now,
    updatedAt: now,
    status: 'visible',
  });

  batch.set(
    doc(threadRef, 'reads', senderUid),
    { uid: senderUid, lastReadAt: now, updatedAt: now },
    { merge: true },
  );

  await batch.commit();
  return { threadId, messageId: messageRef.id };
}


export async function editBazaarDMMessage(threadId: string, messageId: string, nextText: string): Promise<void> {
  const uid = requireAuthUid();
  const id = clean(threadId);
  const mid = clean(messageId);
  const text = clean(nextText).slice(0, 2000);
  if (!id || !mid) throw new Error('Message not found.');
  if (!text) throw new Error('Message cannot be empty.');

  const ref = doc(db, THREADS, id, 'messages', mid);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Message not found.');
  const data = snap.data() as any;
  if (clean(data.senderId) !== uid) throw new Error('You can only edit your own message.');
  if (clean(data.messageType)) throw new Error('Offer history cannot be edited.');
  if (clean(data.status || 'visible') === 'deleted') throw new Error('Deleted messages cannot be edited.');

  const now = nowMs();
  await updateDoc(ref, { text, updatedAt: now, editedAt: now });
}

export async function deleteBazaarDMMessage(threadId: string, messageId: string): Promise<void> {
  const uid = requireAuthUid();
  const id = clean(threadId);
  const mid = clean(messageId);
  if (!id || !mid) throw new Error('Message not found.');

  const ref = doc(db, THREADS, id, 'messages', mid);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Message not found.');
  const data = snap.data() as any;
  if (clean(data.senderId) !== uid) throw new Error('You can only delete your own message.');
  if (clean(data.messageType)) throw new Error('Offer history cannot be deleted.');

  const now = nowMs();
  await updateDoc(ref, { text: '', status: 'deleted', updatedAt: now, deletedAt: now });
}

export async function reportBazaarDMMessage(input: {
  threadId: string;
  message: BazaarDMMessage;
  listingId?: string;
  targetUserId?: string;
  reason: string;
  details?: string;
}): Promise<void> {
  const uid = requireAuthUid();
  const threadId = clean(input.threadId);
  const messageId = clean(input.message?.id);
  const reason = clean(input.reason || 'other').slice(0, 80);
  const details = clean(input.details).slice(0, 1200);
  if (!threadId || !messageId) throw new Error('Message not found.');
  if (clean(input.message?.senderId) === uid) throw new Error('You cannot report your own message.');

  await addDoc(collection(db, 'reports'), stripUndefined({
    reporterUid: uid,
    targetUserId: clean(input.targetUserId) || clean(input.message?.senderId) || undefined,
    targetType: 'bazaar_message',
    reportType: 'bazaar_message',
    threadId,
    messageId,
    listingId: clean(input.listingId) || undefined,
    reason,
    details: details || undefined,
    messageSnapshot: String(input.message?.text || '').slice(0, 2000),
    status: 'open',
    createdAt: nowMs(),
    createdAtServer: serverTimestamp(),
  }));
}

// -----------------------------------------------------------------------------
// Bazaar offer negotiation (listings-only; no Stripe/payment authority)
// -----------------------------------------------------------------------------
export type BazaarOfferStatus = 'pending' | 'countered' | 'agreed' | 'declined';
export type BazaarOfferAction = 'offer' | 'counter' | 'accept' | 'decline';

export interface BazaarDMOffer {
  id: string;
  threadId: string;
  listingId: string;
  buyerId: string;
  sellerId: string;
  amountCents: number;
  currency: string;
  status: BazaarOfferStatus;
  lastAction: BazaarOfferAction;
  lastActionBy: string;
  note?: string;
  createdAt: number;
  updatedAt: number;
}

export function subscribeBazaarDMOffers(
  threadId: string,
  onChange: (offers: BazaarDMOffer[]) => void,
  onError?: (error: unknown) => void,
): Unsubscribe {
  const id = clean(threadId);
  if (!id) { onChange([]); return () => {}; }
  return onSnapshot(
    query(collection(db, THREADS, id, 'offers'), limit(100)),
    (snapshot) => {
      const rows = snapshot.docs.map((row) => ({ id: row.id, ...(row.data() as any) } as BazaarDMOffer))
        .sort((a, b) => Number(a.createdAt || 0) - Number(b.createdAt || 0));
      onChange(rows);
    },
    (error) => { console.error('[bazaarDMs] offer subscription failed', error); onError?.(error); onChange([]); },
  );
}

export async function sendBazaarDMOfferAction(params: {
  currentUser: UserProfile;
  listingId: string;
  listingTitle: string;
  listingImage?: string;
  listingPriceCents?: number;
  listingCurrency?: string;
  sellerId: string;
  sellerName: string;
  sellerAvatar?: string;
  buyerId: string;
  buyerName: string;
  buyerAvatar?: string;
  action: BazaarOfferAction;
  offerId?: string;
  amountCents?: number;
  note?: string;
}): Promise<{ threadId: string; offerId: string; messageId: string }> {
  const senderUid = requireAuthUid();
  const listingId = clean(params.listingId);
  const sellerId = clean(params.sellerId);
  const buyerId = clean(params.buyerId);
  const currency = clean(params.listingCurrency || 'usd').toLowerCase() || 'usd';
  const note = clean(params.note).slice(0, 1000);
  if (!listingId || !sellerId || !buyerId) throw new Error('Bazaar offer details are incomplete.');
  if (sellerId === buyerId || (senderUid !== sellerId && senderUid !== buyerId)) throw new Error('You do not have access to this Bazaar offer.');

  const threadId = getBazaarDMThreadId(listingId, buyerId);
  const threadRef = doc(db, THREADS, threadId);
  const now = nowMs();
  const messageRef = doc(collection(threadRef, 'messages'));
  const action = params.action;

  if (action === 'offer') {
    if (senderUid !== buyerId) throw new Error('Only the buyer can make the first offer.');
    const amountCents = Math.round(Number(params.amountCents || 0));
    if (!Number.isInteger(amountCents) || amountCents < 50) throw new Error('Enter an offer of at least 0.50.');
    const offerRef = doc(collection(threadRef, 'offers'));
    const summary = `Offer ${formatOfferMoney(amountCents, currency)}${note ? ` — ${note}` : ''}`.slice(0, 2000);
    const batch = writeBatch(db);
    const existing = await getDoc(threadRef);
    if (!existing.exists()) {
      batch.set(threadRef, stripUndefined({
        listingId,
        listingTitle: clean(params.listingTitle) || 'Bazaar listing',
        listingImage: clean(params.listingImage) || undefined,
        listingPriceCents: Number.isFinite(Number(params.listingPriceCents)) ? Math.max(0, Math.round(Number(params.listingPriceCents))) : undefined,
        listingCurrency: currency,
        buyerId,
        buyerName: clean(params.buyerName) || 'Buyer',
        buyerAvatar: clean(params.buyerAvatar) || undefined,
        sellerId,
        sellerName: clean(params.sellerName) || 'Seller',
        sellerAvatar: clean(params.sellerAvatar) || undefined,
        participantIds: [buyerId, sellerId].sort(), createdBy: senderUid, createdAt: now, updatedAt: now,
        lastMessageText: summary, lastMessageSenderId: senderUid, lastMessageAt: now, status: 'active',
      }));
    } else {
      batch.update(threadRef, { updatedAt: now, lastMessageText: summary, lastMessageSenderId: senderUid, lastMessageAt: now, status: 'active' });
    }
    batch.set(offerRef, stripUndefined({ threadId, listingId, buyerId, sellerId, amountCents, currency, status: 'pending', lastAction: 'offer', lastActionBy: senderUid, note: note || undefined, createdAt: now, updatedAt: now }));
    batch.set(messageRef, stripUndefined({ threadId, senderId: senderUid, text: summary, messageType: 'offer', offerId: offerRef.id, offerAction: 'offer', amountCents, currency, note: note || undefined, createdAt: now, updatedAt: now, status: 'visible' }));
    batch.set(doc(threadRef, 'reads', senderUid), { uid: senderUid, lastReadAt: now, updatedAt: now }, { merge: true });
    await batch.commit();
    return { threadId, offerId: offerRef.id, messageId: messageRef.id };
  }

  const offerId = clean(params.offerId);
  if (!offerId) throw new Error('Offer not found.');
  const offerRef = doc(threadRef, 'offers', offerId);
  const existingOffer = await getDoc(offerRef);
  if (!existingOffer.exists()) throw new Error('Offer not found.');
  const prior = existingOffer.data() as any;
  if (clean(prior.lastActionBy) === senderUid) throw new Error('Wait for the other person to respond to this offer.');
  if (!['pending', 'countered'].includes(clean(prior.status))) throw new Error('This offer is already closed.');

  const amountCents = action === 'counter' ? Math.round(Number(params.amountCents || 0)) : Math.round(Number(prior.amountCents || 0));
  if (!Number.isInteger(amountCents) || amountCents < 50) throw new Error('Enter an offer of at least 0.50.');
  const nextStatus: BazaarOfferStatus = action === 'counter' ? 'countered' : action === 'accept' ? 'agreed' : 'declined';
  const label = action === 'counter' ? 'Counter offer' : action === 'accept' ? 'Offer accepted' : 'Offer declined';
  const summary = `${label}: ${formatOfferMoney(amountCents, currency)}${note ? ` — ${note}` : ''}`.slice(0, 2000);
  const batch = writeBatch(db);
  batch.update(offerRef, stripUndefined({ amountCents, status: nextStatus, lastAction: action, lastActionBy: senderUid, note: note || prior.note || undefined, updatedAt: now }));
  batch.update(threadRef, { updatedAt: now, lastMessageText: summary, lastMessageSenderId: senderUid, lastMessageAt: now, status: 'active' });
  batch.set(messageRef, stripUndefined({ threadId, senderId: senderUid, text: summary, messageType: 'offer', offerId, offerAction: action, amountCents, currency, note: note || undefined, createdAt: now, updatedAt: now, status: 'visible' }));
  batch.set(doc(threadRef, 'reads', senderUid), { uid: senderUid, lastReadAt: now, updatedAt: now }, { merge: true });
  await batch.commit();
  return { threadId, offerId, messageId: messageRef.id };
}

function formatOfferMoney(cents: number, currency: string) {
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100); }
  catch { return `${currency.toUpperCase()} ${(cents / 100).toFixed(2)}`; }
}
